var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var _a;
import { Component, Vue, Watch } from "vue-facing-decorator";
import { defineAsyncComponent } from "vue";
import { Feature, SystemsClient, SystemComponentsClient, DataTransferClient, SystemSnapshotClient, RequestControllerForceOhmicCommand, GetSystemSnapshotPDFReportQuery, GetSystemSnapshotCSVReportQuery, RequestControllerForceOhmicRequestModel, MeasurementStatusModel, LimitModel, LimitLayoutModel, MeasurementTypeEnum, ComponentStatusEnum, StringStatusEnum, ComponentTypeEnum } from "@/app/api/battery-portal-api";
// Eagerly start fetching the charts chunk at module load so it is available by the time chart data is populated on this immediate-render page.
const snapshotGraphPromise = import(/* webpackChunkName: "charts" */ "@/app/vues/charts/snapshot-graph.vue");
const SnapshotGraphVue = defineAsyncComponent(() => snapshotGraphPromise);
import { ArrayHelper } from "@/app/utils/array-helper";
import { MeasurementHelper } from "@/app/utils/measurement-helper";
import LimitTooltipVue from "@/app/vues/components/limit-tooltip.vue";
import RouteNames from "@/app/router/routes";
import OhmicStringSelectionModalVue from '@/app/vues/company/ohmic-string-selection-modal.vue';
import { HttpHelper } from "@/app/utils/http-helper";
import QueryStringHelper from "@/app/utils/querystring-helper";
import { DownloadUtil } from "@/app/utils/download-util";
import { Notifier } from "../../../common/utils/notifier";
import { CacheHelper } from "@/app/utils/cache-helper";
import { LoginState } from "@/app/store/modules/login.state";
import { State } from "vuex-facing-decorator";
import moment from "moment";
const systemsClient = new SystemsClient(window.apiUrl);
const componentClient = new SystemComponentsClient(window.apiUrl);
const dataTransferClient = new DataTransferClient(window.apiUrl);
const systemSnapshotClient = new SystemSnapshotClient(window.apiUrl);
const queryStringHelper = new QueryStringHelper();
let ControllerRealtimeDataVue = class ControllerRealtimeDataVue extends Vue {
    constructor() {
        super(...arguments);
        this.systems = [];
        this.strings = [];
        this.stringsLoaded = false;
        this.selectedSystem = null;
        this.selectedString = null;
        this.stringTabActive = 'overview';
        this.allStringsPlaceholder = "All Strings";
        this.working = false;
        this.isWaitingForData = false;
        this.isExporting = false;
        this.connection = null;
        this.realtimeData = null;
        this.componentKey = 0;
        this.hiddenCharts = new Array();
        this.displayLimitsState = {};
        this.measurementTypeEnum = MeasurementTypeEnum;
        this.selectedRowComponentId = null;
        this.timeoutHandle = null;
        // Set to true whenever the user changes the selected system, and cleared as soon as
        // the first ReceiveControllerData confirms the controller is reachable. Prevents
        // re-writing the same value to session storage on every subsequent 4s poll.
        this.pendingSystemPersist = false;
        this.moduleMeasurements = [MeasurementTypeEnum.CellTemperature, this.measurementTypeEnum.TerminalTemperature];
        this.baseChartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            throttle: 100,
        };
    }
    mounted() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.loadSystems();
        });
    }
    loadSystems() {
        return __awaiter(this, void 0, void 0, function* () {
            this.working = true;
            try {
                this.systems = (yield systemsClient.getSystems(true, Feature.RealTime)) || [];
                try {
                    if (!this.systems.length)
                        return;
                    // select system from query string if available, it takes priority over the remembered selection
                    if (queryStringHelper.hasParameter("systemId")) {
                        queryStringHelper.setFromQueryString("systemId", null, (value) => {
                            var _a;
                            if (!value || !parseInt(value)) {
                                return null;
                            }
                            this.selectedSystem = this.systems.find(item => item.systemId == value);
                            return (_a = this.selectedSystem) === null || _a === void 0 ? void 0 : _a.systemId;
                        });
                    }
                    else {
                        // otherwise fall back to the last system selected during this browser session
                        const rememberedSystemId = CacheHelper.getSessionItem(RouteNames.RealTime, "SystemId");
                        if (rememberedSystemId) {
                            this.selectedSystem = this.systems.find(item => item.systemId == rememberedSystemId) || null;
                        }
                    }
                }
                catch (ex) {
                    console.error(ex);
                }
            }
            finally {
                this.$nextTick(() => {
                    this.working = false;
                });
            }
        });
    }
    onSystemChanged() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            this.selectedString = null;
            this.strings = [];
            this.stringsLoaded = false;
            // Mark the selection as "needs to be persisted" so the ReceiveControllerData
            // handler stores it exactly once, after the controller confirms it is reachable.
            this.pendingSystemPersist = true;
            let connectToHub = true;
            if (this.working) {
                try {
                    if (queryStringHelper.hasParameter("stringId")) {
                        let stringId = queryStringHelper.getParameter("stringId");
                        connectToHub = !stringId;
                    }
                }
                catch (ex) {
                    console.error(ex);
                }
            }
            else {
                // Do NOT push the systemId into the URL yet: if the controller turns out to be
                // offline the user would end up re-selecting it on every refresh. The URL is
                // updated together with the session storage entry, only after the first
                // ReceiveControllerData confirms the controller is reachable. If the selection
                // was cleared, however, we DO want the URL cleaned up immediately.
                if (!((_a = this.selectedSystem) === null || _a === void 0 ? void 0 : _a.systemId)) {
                    queryStringHelper.updateQueryString(null, "systemId");
                    queryStringHelper.updateQueryString(null, "stringId");
                }
            }
            if (this.selectedSystem && this.selectedSystem.systemId) {
                this.isWaitingForData = true;
                this.getStrings();
                if (connectToHub)
                    this.connectToHub();
            }
            else {
                this.disconnectHub();
            }
        });
    }
    getStrings() {
        this.working = true;
        componentClient
            .getSystemStrings(this.selectedSystem.systemId)
            .then((response) => {
            this.strings = response || [];
            try {
                if (queryStringHelper.hasParameter("stringId")) {
                    let stringId = queryStringHelper.getParameter("stringId");
                    if (stringId)
                        this.selectString(this.strings.find(item => item.componentId == stringId) || null);
                }
            }
            catch (ex) {
                console.error(ex);
            }
        })
            .catch((error) => {
            HttpHelper.handleResponse(ex, () => {
                Notifier.onServerError(ex);
            });
            this.strings = [];
        })
            .finally(() => {
            this.working = false;
            this.stringsLoaded = true;
        });
    }
    selectString(string) {
        var _a;
        this.isWaitingForData = true;
        this.selectedString = string;
        setTimeout(() => {
            this.connectToHub();
        }, 100);
        queryStringHelper.updateQueryString((_a = this.selectedString) === null || _a === void 0 ? void 0 : _a.componentId, "stringId");
    }
    stringClass(item) {
        var _a, _b;
        if (!((_b = (_a = this.realtimeData) === null || _a === void 0 ? void 0 : _a.alarmSummaries) === null || _b === void 0 ? void 0 : _b.length))
            return "";
        let alarmSummary = this.realtimeData.alarmSummaries[0];
        var stringState = alarmSummary.stringStates.find((str) => str.componentId === item.componentId);
        if (!stringState)
            return "";
        return `${stringState.stringStatusName}`.toLowerCase();
    }
    matchingSnapshots(item) {
        var _a;
        if (!((_a = this.realtimeData) === null || _a === void 0 ? void 0 : _a.stringsSummary))
            return [];
        let result = this.realtimeData.stringsSummary.filter((match) => {
            return match.stringId === item.componentId;
        });
        if (!result.length)
            return [];
        return result;
    }
    ;
    /**
     * Reacts to the hub refusing the caller. Returns true when handled: a 401 from negotiate means
     * the session has expired (redirect to login), and a HubException carries a message for the
     * user, such as not having access to the selected system.
     */
    handleHubAuthError(err) {
        var _a;
        if (this.isHubUnauthorized(err)) {
            HttpHelper.handleResponse({ status: 401 }, () => { });
            return true;
        }
        // The server's HubException message arrives as "... HubException: <message>".
        const match = /HubException: (.*)$/.exec((_a = err === null || err === void 0 ? void 0 : err.message) !== null && _a !== void 0 ? _a : "");
        if (match) {
            Notifier.onError(match[1]);
            return true;
        }
        return false;
    }
    ;
    /**
     * True when the hub rejected the caller as not logged in. SignalR wraps a failed negotiate in
     * FailedToNegotiateWithServerError, which keeps only the text ("... Status code '401'") and not
     * the status code, so both forms are checked.
     */
    isHubUnauthorized(err) {
        var _a;
        return (err === null || err === void 0 ? void 0 : err.statusCode) === 401 || /Status code '401'/.test((_a = err === null || err === void 0 ? void 0 : err.message) !== null && _a !== void 0 ? _a : "");
    }
    ;
    connectToHub() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.selectedSystem) {
                this.disconnectHub();
                this.isWaitingForData = false;
                return;
            }
            this.realtimeData = null;
            if (this.selectedString)
                this.stringTabActive = 'graphs';
            else
                this.stringTabActive = 'overview';
            // Disconnect previous connection if any
            if (this.connection) {
                yield this.disconnectHub();
            }
            // Set waiting AFTER disconnecting the previous connection: the old
            // onclose handler fires during disconnectHub() and would otherwise
            // immediately reset waiting back to false, hiding the overlay.
            this.isWaitingForData = true;
            // Build selection payload
            const selection = {
                systemId: this.selectedSystem.systemId,
                stringId: this.selectedString ? this.selectedString.componentId : null,
                useImperialMeasurementSystem: this.loginState.useImperialMeasurementSystem
            };
            // Create new connection (lazy-load SignalR chunk on first use).
            // Custom retry policy: retry aggressively for the first few seconds then
            // fall back to every 30s for up to ~5 minutes, so brief Wi-Fi/VPN drops recover
            // without forcing the user to reload the page.
            const signalRModule = yield import(/* webpackChunkName: "signalr" */ "@microsoft/signalr");
            const retryDelaysMs = [0, 2000, 5000, 10000, 15000, 30000];
            this.connection = new signalRModule.HubConnectionBuilder()
                .withUrl("/controller-realtime-data-hub")
                .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (ctx) => {
                    // Session expired: retrying cannot succeed, send the user to the login page.
                    if (this.isHubUnauthorized(ctx.retryReason)) {
                        this.handleHubAuthError(ctx.retryReason);
                        return null;
                    }
                    // Stop retrying after ~5 minutes of elapsed disconnected time.
                    if (ctx.elapsedMilliseconds > 5 * 60 * 1000)
                        return null;
                    return retryDelaysMs[Math.min(ctx.previousRetryCount, retryDelaysMs.length - 1)];
                }
            })
                .build();
            // Capture the connection we just built so async callbacks below can verify
            // they're still acting on the current connection (guards against races when
            // the user quickly changes selection and connectToHub() is called again).
            const currentConnection = this.connection;
            let dataReceived = false;
            // Notification coalescing state.
            // - reconnectingNotified: prevents stacking multiple "Trying to reconnect…" toasts
            //   when the connection flaps quickly (e.g., laptop lock/unlock, VPN drops).
            // - reconnectedTimer: delays the "Reconnected" toast until the connection has
            //   been stable for a short window, so a burst of connect/disconnect cycles
            //   collapses into a single final "Reconnected" message.
            let reconnectingNotified = false;
            let reconnectedTimer = null;
            const RECONNECTED_STABLE_MS = 2000;
            const clearReconnectedTimer = () => {
                if (reconnectedTimer !== null) {
                    window.clearTimeout(reconnectedTimer);
                    reconnectedTimer = null;
                }
            };
            // Handlers registered via .on() persist across automatic reconnects — the
            // underlying HubConnection object is reused, so we do NOT need to re-hook them.
            this.connection.on("ReceiveControllerData", (data) => {
                var _a;
                console.log('ReceiveControllerData', data);
                dataReceived = true;
                this.isWaitingForData = false;
                // Persist the selection only the first time we confirm the controller is
                // reachable after a system change. Subsequent polls skip the write.
                if (this.pendingSystemPersist && ((_a = this.selectedSystem) === null || _a === void 0 ? void 0 : _a.systemId)) {
                    CacheHelper.storeSessionItem(RouteNames.RealTime, "SystemId", this.selectedSystem.systemId);
                    // Also reflect the confirmed selection in the URL so a refresh restores
                    // this system; if it were pushed on selection change, an offline system
                    // would trap the user on every refresh.
                    queryStringHelper.updateQueryString(this.selectedSystem.systemId, "systemId");
                    this.pendingSystemPersist = false;
                }
                const shouldUpdateCharts = this.realtimeData == null ||
                    !this.deepEqual(this.realtimeData, data);
                if (shouldUpdateCharts) {
                    this.updateData(data);
                    setTimeout(() => {
                        this.updateCharts();
                    }, 10);
                }
            });
            this.connection.on("ErrorOnReceivingControllerData", (data) => {
                var _a;
                console.error('ErrorOnReceivingControllerData', data);
                var msg = (_a = data.error) !== null && _a !== void 0 ? _a : "Error requesting Controller Real-time data";
                Notifier.onError(msg);
                this.disconnectHub();
                dataReceived = true;
                this.isWaitingForData = false;
            });
            // Warn the user while the client is trying to recover the connection.
            this.connection.onreconnecting((error) => {
                console.warn('SignalR reconnecting', error);
                if (currentConnection !== this.connection)
                    return;
                this.isWaitingForData = true;
                // A pending "Reconnected" toast for a previous flap is no longer relevant.
                clearReconnectedTimer();
                // Only surface the warning once per outage so rapid flap cycles don't
                // stack repeated "Trying to reconnect…" messages in the notifier.
                if (!reconnectingNotified) {
                    reconnectingNotified = true;
                    Notifier.onWarning("Connection to the server was lost. Trying to reconnect…");
                }
            });
            // After a successful reconnect the server has no record of our previous
            // subscription (new connection id on the server side), so re-subscribe.
            this.connection.onreconnected((connectionId) => __awaiter(this, void 0, void 0, function* () {
                console.log('SignalR reconnected', connectionId);
                if (currentConnection !== this.connection)
                    return;
                try {
                    yield currentConnection.invoke("SubscribeSelections", selection);
                    // Delay the success toast until the connection stays up for a short
                    // window; if we flap again before then, the timer is cancelled and
                    // no "Reconnected" toast is shown for this transient recovery.
                    clearReconnectedTimer();
                    reconnectedTimer = window.setTimeout(() => {
                        reconnectedTimer = null;
                        reconnectingNotified = false;
                        Notifier.onOk("Reconnected to the server.");
                    }, RECONNECTED_STABLE_MS);
                }
                catch (err) {
                    console.error("Failed to re-subscribe after reconnect:", err);
                    if (!this.handleHubAuthError(err))
                        Notifier.onError("Reconnected, but failed to resume the real-time data stream. Please reload the page.");
                    this.isWaitingForData = false;
                }
            }));
            // Fires when reconnection attempts are exhausted (or the connection is
            // stopped intentionally). Only notify if this is still the active connection
            // AND we didn't stop it ourselves.
            this.connection.onclose((error) => {
                if (!currentConnection.__intentionalClose) {
                    console.warn('SignalR connection closed', error);
                }
                if (currentConnection !== this.connection)
                    return;
                this.isWaitingForData = false;
                this.connection = null;
                clearReconnectedTimer();
                reconnectingNotified = false;
                if (this.timeoutHandle) {
                    window.clearTimeout(this.timeoutHandle);
                    this.timeoutHandle = null;
                }
                if (error) {
                    Notifier.onError("Real-time connection was closed and could not be restored. Please check your network and reload the page.");
                }
            });
            try {
                yield this.connection.start();
                yield this.connection.invoke("SubscribeSelections", selection);
            }
            catch (err) {
                console.error("Failed to start real-time connection:", err);
                if (!this.handleHubAuthError(err))
                    Notifier.onError("Unable to establish a real-time connection to the server. Please check your network and try again.");
                this.isWaitingForData = false;
                yield this.disconnectHub();
                return;
            }
            this.timeoutHandle = window.setTimeout(() => {
                if (!dataReceived) {
                    this.isWaitingForData = false;
                    console.error("No real-time data received within the expected time frame.");
                    Notifier.onError("No real-time data was received from the server within 120 seconds. Please check your connection or try again.");
                }
                this.timeoutHandle = null;
            }, 120000);
        });
    }
    beforeRouteLeave(to, from, next) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.disconnectHub();
            next();
        });
    }
    /// <summary>
    /// Aborts the current waiting state and drops the selected system. Bound to the
    /// Cancel button shown inside the "Waiting for data..." overlay so the user can
    /// escape when the selected controller is unreachable, without having to reload.
    /// </summary>
    cancelWaiting() {
        return __awaiter(this, void 0, void 0, function* () {
            this.isWaitingForData = false;
            yield this.disconnectHub();
            this.selectedSystem = null;
            // Clear any previously-persisted selection so a refresh does not put the user
            // back into the same waiting state.
            CacheHelper.removeSessionItem(RouteNames.RealTime, "SystemId");
            queryStringHelper.updateQueryString(null, "systemId");
            queryStringHelper.updateQueryString(null, "stringId");
        });
    }
    disconnectHub() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.connection) {
                try {
                    // Clear timeout if exists
                    if (this.timeoutHandle) {
                        window.clearTimeout(this.timeoutHandle);
                        this.timeoutHandle = null;
                    }
                    this.connection.__intentionalClose = true;
                    yield this.connection.stop();
                }
                catch (error) {
                    console.error("Error disconnecting hub:", error);
                }
                finally {
                    this.connection = null;
                    this.realtimeData = null;
                }
            }
        });
    }
    beforeUnmount() {
        this.disconnectHub();
    }
    getComponentKey(index) {
        return this.componentKey + 999 + index;
    }
    updateData(data) {
        // Save current table items expanded state
        var tableItemsState = {};
        if (this.realtimeData) {
            this.realtimeData.stringTable.forEach((item) => {
                tableItemsState[item.moduleId] = item.expanded;
            });
        }
        this.realtimeData = data;
        // Restore table items expanded state
        this.realtimeData.stringTable.forEach((item) => {
            if (tableItemsState.hasOwnProperty(item.moduleId)) {
                item.expanded = tableItemsState[item.moduleId];
            }
            else {
                item.expanded = false;
            }
        });
    }
    updateCharts() {
        this.componentKey++;
    }
    get hasStrings() {
        return this.strings && this.strings.length ? true : false;
        ;
    }
    ;
    get showNoSystemsMessage() {
        return !this.working &&
            this.systems.length === 0 &&
            this.loginState.isClientInstaller;
    }
    ;
    get componentTitle() {
        return this.selectedString ? (this.selectedString.componentName || `${this.selectedString.componentId}`) : this.allStringsPlaceholder;
    }
    ;
    constructChartYAxes(chart) {
        let chartOptions = Object.assign(Object.assign({}, this.baseChartOptions), { animation: false, plugins: {
                legend: {
                    display: false
                },
                tooltip: {
                    callbacks: {
                        title: (tooltipItem) => {
                            var measurementTypeId = tooltipItem[0].dataset.measurementTypeId || 0;
                            if (this.isLithiumSamsungGen3Measurement(measurementTypeId)) {
                                return `Module ${tooltipItem[0].label} Sensor ${tooltipItem[0].dataset.datasetId}`;
                            }
                            var blockUoM = [MeasurementTypeEnum.BlockVoltage, MeasurementTypeEnum.Temperature, MeasurementTypeEnum.Ohmic, MeasurementTypeEnum.OhmicRise];
                            if (blockUoM.includes(measurementTypeId))
                                if (this.realtimeData.isLithium)
                                    return `Cell ${tooltipItem[0].label}`;
                                else
                                    return `Block ${tooltipItem[0].label}`;
                            return `String ${tooltipItem[0].label}`;
                        },
                        label: this.formatChartLabel
                    }
                },
            }, scales: {
                x: {
                    grid: {
                        display: false
                    },
                    ticks: {
                        autoSkip: true,
                        stepSize: 1,
                        maxRotation: 0,
                        minRotation: 0
                    }
                },
                xLimit: {
                    type: 'linear',
                    display: false,
                    grid: {
                        display: false
                    },
                    ticks: {
                        autoSkip: false,
                        stepSize: 0.5
                    },
                    offset: false
                },
                y: {
                    type: 'linear',
                    suggestedMin: 0,
                    suggestedMax: 0,
                    beginAtZero: false
                }
            }, onClick: this.onBarClick, onHover: (e, el) => {
                if (!e || !el || !el[0])
                    return;
                let section = el[0];
                let currentStyle = e.native.target.style;
                currentStyle.cursor = section && this.selectedString ? "pointer" : "normal";
            } });
        var axis = chartOptions.scales.y;
        if (chart.minValue) {
            axis.suggestedMin = chart.minValue;
        }
        if (chart.maxValue) {
            axis.suggestedMax = chart.maxValue;
        }
        return chartOptions;
    }
    onBarClick(event, elements) {
        var _a, _b, _c, _d, _e, _f, _g;
        if (!elements || !this.selectedString)
            return;
        let element = elements[0];
        if (!element || !((_a = event === null || event === void 0 ? void 0 : event.chart) === null || _a === void 0 ? void 0 : _a.data))
            return;
        let idx = element.index;
        let dataSetIdx = element.datasetIndex;
        let chart = event.chart;
        if (!chart.data.datasets || !chart.data.datasets.length)
            return;
        let dataSet = chart.data.datasets[dataSetIdx];
        if (!dataSet || !dataSet.ids || !dataSet.ids.length)
            return;
        let measurementTypeId = dataSet.measurementTypeId || 0;
        let blockId = dataSet.ids[idx] || 0;
        let stringStatusId = (_d = (_c = (_b = this.realtimeData) === null || _b === void 0 ? void 0 : _b.stringSnapshot) === null || _c === void 0 ? void 0 : _c.stringStatusId) !== null && _d !== void 0 ? _d : StringStatusEnum.Unknown;
        let params = {
            systemId: (_e = this.selectedString) === null || _e === void 0 ? void 0 : _e.systemId.toString(),
            stringId: this.selectedString.componentId.toString(),
            stringStatusId: this.selectedString.stringStatusId ? this.string.stringStatusId.toString() : StringStatusEnum.Unknown.toString(),
            blockId: blockId.toString(),
            measurementTypeId: measurementTypeId.toString()
        };
        if (event.native.ctrlKey || event.native.metaKey) {
            const routeData = this.$router.resolve({
                name: RouteNames.TrendForMeasurement,
                params
            });
            window.open(routeData.href, '_blank');
        }
        else {
            this.$router.replace({
                name: RouteNames.RealTime,
                query: {
                    systemId: (_f = this.selectedString) === null || _f === void 0 ? void 0 : _f.systemId,
                    stringId: (_g = this.selectedString) === null || _g === void 0 ? void 0 : _g.componentId
                }
            });
            this.$nextTick(() => {
                setTimeout(() => {
                    this.$router.push({
                        name: RouteNames.TrendForMeasurement,
                        params
                    });
                }, 1);
            });
        }
    }
    ;
    formatChartLabel(tooltipItem) {
        let label = tooltipItem.dataset.label || '';
        if (label) {
            label += ': ';
        }
        if (tooltipItem.parsed.y !== null) {
            var formattedValue = MeasurementHelper.getFormattedValueForUnitOfMeasurement(tooltipItem.parsed.y, tooltipItem.dataset.measurementTypeId);
            label += formattedValue;
        }
        return label;
    }
    changeChartVisibility(index) {
        let idx = ArrayHelper.findFirstIndex(this.hiddenCharts, (item) => {
            return item == index;
        });
        if (idx < 0)
            this.hiddenCharts.push(index);
        else
            this.hiddenCharts.splice(idx, 1);
    }
    checkVisibility(index) {
        let idx = ArrayHelper.findFirstIndex(this.hiddenCharts, (item) => {
            return item == index;
        });
        return idx < 0 ? true : false;
    }
    hasLimits(chartData) {
        return chartData.annotations && chartData.annotations.length > 0;
    }
    onDisplayLimitsChanged(index, state) {
        this.displayLimitsState[index] = state;
    }
    displayLimit(index, chartData) {
        if (!this.hasLimits(chartData))
            return false;
        if (typeof this.displayLimitsState[index] === 'boolean')
            return this.displayLimitsState[index];
        return true;
    }
    deepEqual(obj1, obj2, path = '') {
        if (obj1 === obj2)
            return true;
        if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
            return false;
        }
        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);
        if (keys1.length !== keys2.length) {
            return false;
        }
        for (let key of keys1) {
            const newPath = path ? `${path}.${key}` : key;
            if (!keys2.includes(key)) {
                return false;
            }
            if (!this.deepEqual(obj1[key], obj2[key], newPath))
                return false;
        }
        return true;
    }
    // Methods for UI
    get systemSummary() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
        // System-level: stringId is null
        const summary = Array.isArray((_a = this.realtimeData) === null || _a === void 0 ? void 0 : _a.alarmSummaries)
            ? this.realtimeData.alarmSummaries.find((x) => x.stringId == null)
            : null;
        return {
            warning: (_b = summary === null || summary === void 0 ? void 0 : summary.totalWarningAlarms) !== null && _b !== void 0 ? _b : 0,
            critical: (_c = summary === null || summary === void 0 ? void 0 : summary.totalCriticalAlarms) !== null && _c !== void 0 ? _c : 0,
            system: (_d = summary === null || summary === void 0 ? void 0 : summary.totalSystemAlarms) !== null && _d !== void 0 ? _d : 0,
            stringsHealthy: (_e = summary === null || summary === void 0 ? void 0 : summary.totalHealthyStrings) !== null && _e !== void 0 ? _e : 0,
            stringsWarning: (_f = summary === null || summary === void 0 ? void 0 : summary.totalWarningStrings) !== null && _f !== void 0 ? _f : 0,
            stringsCritical: (_g = summary === null || summary === void 0 ? void 0 : summary.totalCriticalStrings) !== null && _g !== void 0 ? _g : 0,
            stringsTotal: ((_h = summary === null || summary === void 0 ? void 0 : summary.totalHealthyStrings) !== null && _h !== void 0 ? _h : 0) + ((_j = summary === null || summary === void 0 ? void 0 : summary.totalWarningStrings) !== null && _j !== void 0 ? _j : 0) + ((_k = summary === null || summary === void 0 ? void 0 : summary.totalCriticalStrings) !== null && _k !== void 0 ? _k : 0),
            blocksHealthy: (_l = summary === null || summary === void 0 ? void 0 : summary.totalHealthyBlocks) !== null && _l !== void 0 ? _l : 0,
            blocksWarning: (_m = summary === null || summary === void 0 ? void 0 : summary.totalWarningBlocks) !== null && _m !== void 0 ? _m : 0,
            blocksCritical: (_o = summary === null || summary === void 0 ? void 0 : summary.totalCriticalBlocks) !== null && _o !== void 0 ? _o : 0,
            blocksTotal: ((_p = summary === null || summary === void 0 ? void 0 : summary.totalHealthyBlocks) !== null && _p !== void 0 ? _p : 0) + ((_q = summary === null || summary === void 0 ? void 0 : summary.totalWarningBlocks) !== null && _q !== void 0 ? _q : 0) + ((_r = summary === null || summary === void 0 ? void 0 : summary.totalCriticalBlocks) !== null && _r !== void 0 ? _r : 0),
        };
    }
    ;
    get stringSnapshot() {
        return this.realtimeData.stringSnapshot || {};
    }
    get systemSnapshot() {
        return this.realtimeData.systemSnapshot || {};
    }
    get stringSummary() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
        // String-level: stringId is not null
        const summary = Array.isArray((_a = this.realtimeData) === null || _a === void 0 ? void 0 : _a.alarmSummaries)
            ? this.realtimeData.alarmSummaries.find((x) => x.stringId != null)
            : null;
        return {
            system: (_b = summary === null || summary === void 0 ? void 0 : summary.totalSystemAlarms) !== null && _b !== void 0 ? _b : 0,
            blocksHealthy: (_c = summary === null || summary === void 0 ? void 0 : summary.totalHealthyBlocks) !== null && _c !== void 0 ? _c : 0,
            blocksWarning: (_d = summary === null || summary === void 0 ? void 0 : summary.totalWarningBlocks) !== null && _d !== void 0 ? _d : 0,
            blocksCritical: (_e = summary === null || summary === void 0 ? void 0 : summary.totalCriticalBlocks) !== null && _e !== void 0 ? _e : 0,
            blocksTotal: (_f = summary === null || summary === void 0 ? void 0 : summary.totalBlocks) !== null && _f !== void 0 ? _f : 0,
            modulesHealthy: (_g = summary === null || summary === void 0 ? void 0 : summary.totalHealthyModules) !== null && _g !== void 0 ? _g : 0,
            modulesWarning: (_h = summary === null || summary === void 0 ? void 0 : summary.totalWarningModules) !== null && _h !== void 0 ? _h : 0,
            modulesCritical: (_j = summary === null || summary === void 0 ? void 0 : summary.totalCriticalModules) !== null && _j !== void 0 ? _j : 0,
            modulesTotal: (_k = summary === null || summary === void 0 ? void 0 : summary.totalModules) !== null && _k !== void 0 ? _k : 0,
            stringState: (_l = summary === null || summary === void 0 ? void 0 : summary.stringState) !== null && _l !== void 0 ? _l : "Unknown",
            stringCriticalAlarms: (_m = summary === null || summary === void 0 ? void 0 : summary.totalStringCriticalAlarms) !== null && _m !== void 0 ? _m : 0,
            stringWarningAlarms: (_o = summary === null || summary === void 0 ? void 0 : summary.totalStringWarningAlarms) !== null && _o !== void 0 ? _o : 0,
        };
    }
    ;
    getValue(value, digits = 2) {
        return MeasurementHelper.getRoundedValueString(value, digits);
    }
    ;
    getVoltageValue(value) {
        return MeasurementHelper.formatVoltage(value);
    }
    ;
    getCurrentValue(value) {
        return MeasurementHelper.formatCurrent(value);
    }
    measurementState(measurementType, stringId) {
        let state = this.realtimeData.limits.filter(limit => limit.componentId == stringId &&
            limit.componentTypeId == ComponentTypeEnum.String &&
            limit.measurementTypeId == measurementType)[0];
        if (measurementType == MeasurementTypeEnum.BlockVoltageVariation) {
            if (state == null) {
                state = this.realtimeData.limits.filter(limit => limit.componentId == stringId &&
                    limit.componentTypeId == ComponentTypeEnum.String &&
                    limit.measurementTypeId == MeasurementTypeEnum.CellVoltageVariation)[0];
            }
            if (state)
                state.mesaurementTypeName = 'Cell Voltage Variation';
        }
        return state;
    }
    ;
    measurementClassByValue(measurementType, value) {
        var _a;
        const cssClass = "number";
        let measurement = this.measurementState(measurementType, (_a = this.realtimeData) === null || _a === void 0 ? void 0 : _a.stringId);
        if (!(measurement === null || measurement === void 0 ? void 0 : measurement.limit))
            return cssClass;
        let currentState = "";
        if (measurement.limit.upperWarningLimit != null && value >= measurement.limit.upperWarningLimit)
            currentState = "warning";
        if (measurement.limit.upperCriticalLimit != null && value >= measurement.limit.upperCriticalLimit)
            currentState = "critical";
        if (measurement.limit.lowerWarningLimit != null && value <= measurement.limit.lowerWarningLimit)
            currentState = "warning";
        if (measurement.limit.lowerWarningLimit != null && value <= measurement.limit.lowerWarningLimit)
            currentState = "critical";
        return `${cssClass} ${currentState || ""}`.trim().toLowerCase();
    }
    createMeasurementForModuleVoltageVariation(item) {
        try {
            var model = new MeasurementStatusModel();
            model.limit = new LimitModel();
            model.limit.upperCriticalLimit = item.moduleVoltageVariationHighCrictialLimit;
            model.measurementTypeName = 'Lithium Module Voltage Variation';
            model.limit.layout = new LimitLayoutModel();
            model.limit.layout.showHighCritical = true;
            return model;
        }
        catch (e) {
            console.error('createMeasurementForModuleVoltageVariation error', e);
        }
    }
    createMeasurementForModuleVoltageSense(item) {
        var model = new MeasurementStatusModel();
        model.limit = new LimitModel();
        model.limit.upperWarningLimit = item.moduleVoltageSenseHighWarningLimit;
        model.measurementTypeName = 'Lithium Module Voltage Sense Diff';
        model.limit.layout = new LimitLayoutModel();
        model.limit.layout.showHighWarning = true;
        return model;
    }
    createMeasurementForModuleBlock(item, measurementType) {
        var model = new MeasurementStatusModel();
        model.limit = new LimitModel();
        model.limit.layout = new LimitLayoutModel();
        try {
            switch (measurementType) {
                case MeasurementTypeEnum.BlockVoltage:
                    model.limit.upperCriticalLimit = item.voltageHighCriticalLimit;
                    model.limit.lowerCriticalLimit = item.voltageLowCriticalLimit;
                    model.measurementTypeName = 'Lithium Block Voltage';
                    break;
                case MeasurementTypeEnum.Temperature:
                    model.limit.upperCriticalLimit = item.temperatureHighCriticalLimit;
                    model.limit.lowerCriticalLimit = item.temperatureLowCriticalLimit;
                    model.limit.upperWarningLimit = item.temperatureHighWarningLimit;
                    model.limit.lowerWarningLimit = item.temperatureLowWarningLimit;
                    model.measurementTypeName = 'Lithium Block Temperature';
                    model.limit.layout.showHighWarning = true;
                    model.limit.layout.showLowWarning = true;
                    break;
                case MeasurementTypeEnum.CellTemperature:
                    model.limit.upperCriticalLimit = item.cellTempHighCriticalLimit;
                    model.limit.lowerCriticalLimit = item.cellTempLowCriticalLimit;
                    model.limit.upperWarningLimit = item.cellTempHighWarningLimit;
                    model.limit.lowerWarningLimit = item.cellTempLowWarningLimit;
                    model.measurementTypeName = 'Cell Temperature';
                    model.limit.layout.showHighWarning = true;
                    model.limit.layout.showLowWarning = true;
                    break;
                case MeasurementTypeEnum.TerminalTemperature:
                    model.limit.upperCriticalLimit = item.terminalTempHighCriticalLimit;
                    model.limit.upperWarningLimit = item.terminalTempHighWarningLimit;
                    model.measurementTypeName = 'Terminal Temperature';
                    model.limit.layout.showHighWarning = true;
                    break;
            }
            model.limit.layout.showHighCritical = true;
            model.limit.layout.showLowCritical = true;
        }
        catch (e) {
            console.error('createMeasurementForModuleBlock error', e);
        }
        return model;
    }
    measurementClassForMeasurementType(item, measurementType) {
        switch (measurementType) {
            case MeasurementTypeEnum.BlockVoltage:
                if (item.voltage == null || (item.voltageHighCriticalLimit == null && item.voltageLowCriticalLimit == null))
                    return "";
                if ((item.voltageLowCriticalLimit != null && item.voltage < item.voltageLowCriticalLimit) || (item.voltageHighCriticalLimit != null && item.voltage >= item.voltageHighCriticalLimit))
                    return "critical";
                break;
            case MeasurementTypeEnum.Temperature:
                if (item.temperature == null || (item.temperatureHighCriticalLimit == null && item.temperatureLowCriticalLimit == null && item.temperatureHighWarningLimit == null && item.temperatureLowWarningLimit == null))
                    return "";
                if ((item.temperatureLowCriticalLimit != null && item.temperature <= item.temperatureLowCriticalLimit) || (item.temperatureHighCriticalLimit != null && item.temperature >= item.temperatureHighCriticalLimit))
                    return "critical";
                else if ((item.temperatureLowWarningLimit != null && item.temperature <= item.temperatureLowWarningLimit) || (item.temperatureHighWarningLimit != null && item.temperature >= item.temperatureHighWarningLimit))
                    return "warning";
                break;
            case MeasurementTypeEnum.CellTemperature:
                if (item.cellTemperature == null || (item.cellTempLowCriticalLimit == null && item.cellTempLowCriticalLimit == null && item.cellTempHighWarningLimit == null && item.cellTempLowWarningLimit == null))
                    return "";
                if ((item.cellTempLowCriticalLimit != null && item.cellTemperature <= item.cellTempLowCriticalLimit) || (item.cellTempHighCriticalLimit != null && item.cellTemperature >= item.cellTempHighCriticalLimit))
                    return "critical";
                else if ((item.cellTempLowWarningLimit != null && item.temperature <= item.cellTempLowWarningLimit) || (item.cellTempHighWarningLimit != null && item.cellTemperature >= item.cellTempHighWarningLimit))
                    return "warning";
                break;
            case MeasurementTypeEnum.TerminalTemperature:
                if (item.terminalTemperature == null || (item.terminalTempHighWarningLimit == null && item.terminalTempLowWarningLimit == null))
                    return "";
                if (item.terminalTempHighCriticalLimit != null && item.terminalTemperature >= item.terminalTempHighCriticalLimit)
                    return "critical";
                else if (item.terminalTempHighWarningLimit != null && item.terminalTemperature >= item.terminalTempHighWarningLimit)
                    return "warning";
                break;
        }
        return "";
    }
    getModuleRowClass(item) {
        var moduleRowClass = "";
        var assignModuleRowClass = function (measurementClass) {
            moduleRowClass = moduleRowClass == "critical" ? moduleRowClass : measurementClass == "critical" ? measurementClass : moduleRowClass || measurementClass;
        };
        assignModuleRowClass(this.getModuleVoltageVariationClass(item));
        assignModuleRowClass(this.getModuleVoltageSenseDiffClass(item));
        item.blocks.forEach((subitem) => {
            assignModuleRowClass(this.measurementClassForMeasurementType(subitem, MeasurementTypeEnum.BlockVoltage));
            assignModuleRowClass(this.measurementClassForMeasurementType(subitem, MeasurementTypeEnum.Temperature));
        });
        if (this.realtimeData.isLithiumSamsungGen3) {
            item.moduleSensors.forEach((subitem) => {
                assignModuleRowClass(this.measurementClassForMeasurementType(subitem, MeasurementTypeEnum.CellTemperature));
                assignModuleRowClass(this.measurementClassForMeasurementType(subitem, MeasurementTypeEnum.TerminalTemperature));
            });
        }
        return moduleRowClass;
    }
    getModuleSensorClass(item) {
        var cellTempClass = this.measurementClassForMeasurementType(item, MeasurementTypeEnum.CellTemperature);
        var termTempClass = this.measurementClassForMeasurementType(item, MeasurementTypeEnum.TerminalTemperature);
        if (cellTempClass == "critical" || termTempClass == "critical")
            return "critical";
        if (cellTempClass == "warning" || termTempClass == "warning")
            return "warning";
        return "";
    }
    getModuleSensorsClass(item) {
        if (!(item === null || item === void 0 ? void 0 : item.moduleSensors))
            return "";
        var hasCritical = false, hasWarning = false;
        item.moduleSensors.forEach((sensor) => {
            var sensorClass = this.getModuleSensorClass(sensor);
            hasCritical = hasCritical || sensorClass == "critical";
            hasWarning = hasWarning || sensorClass == "warning";
        });
        return hasCritical ? "critical" : hasWarning ? "warning" : "";
    }
    getSensorsRange(item) {
        var _a;
        if (((_a = item.moduleSensors) === null || _a === void 0 ? void 0 : _a.length) < 1)
            return "";
        return `${item.moduleSensors[0].componentCode} - ${item.moduleSensors[item.moduleSensors.length - 1].componentCode}`;
    }
    getSensorsCellTempRange(item) {
        var _a;
        if (((_a = item.moduleSensors) === null || _a === void 0 ? void 0 : _a.length) < 1)
            return "";
        return `${this.getValue(item.moduleSensors[0].cellTemperature, 1)} - ${this.getValue(item.moduleSensors[item.moduleSensors.length - 1].cellTemperature, 1)}`;
    }
    getSensorsTerminalTempRange(item) {
        var _a;
        if (((_a = item.moduleSensors) === null || _a === void 0 ? void 0 : _a.length) < 1)
            return "";
        return `${this.getValue(item.moduleSensors[0].terminalTemperature, 1)} - ${this.getValue(item.moduleSensors[item.moduleSensors.length - 1].terminalTemperature, 1)}`;
    }
    getModuleVoltageVariationClass(item) {
        if (!(item === null || item === void 0 ? void 0 : item.blocks) || !item.voltageDelta || !(item === null || item === void 0 ? void 0 : item.moduleVoltageVariationHighCrictialLimit))
            return "";
        if (item.voltageDelta >= (item.moduleVoltageVariationHighCrictialLimit))
            return "critical";
        return "";
    }
    getModuleVoltageSenseDiffClass(item) {
        if (!(item === null || item === void 0 ? void 0 : item.blocks) || !item.moduleVoltageSenseVariation || !(item === null || item === void 0 ? void 0 : item.moduleVoltageSenseHighWarningLimit))
            return "";
        if (item.moduleVoltageSenseVariation >= item.moduleVoltageSenseHighWarningLimit)
            return "warning";
        return "";
    }
    getStatusClass(statusId) {
        switch (statusId) {
            case ComponentStatusEnum.Critical:
                return "critical";
            case ComponentStatusEnum.Warning:
                return "warning";
        }
        return "";
    }
    getModuleBlocksClass(item) {
        if (!(item === null || item === void 0 ? void 0 : item.blocks))
            return "";
        var hasCritical = false;
        var hasWarning = false;
        item.blocks.forEach((block) => {
            var blockClass = this.getModuleBlockClass(block);
            hasCritical = hasCritical || blockClass == "critical";
            hasWarning = hasWarning || blockClass == "warning";
        });
        return hasCritical ? "critical" : hasWarning ? "warning" : "";
    }
    getModuleBlockClass(item) {
        if (!item.voltage || (!item.voltageHighCriticalLimit && !item.voltageLowCriticalLimit))
            return "";
        if ((item.voltageLowCriticalLimit != null && item.voltage <= item.voltageLowCriticalLimit) ||
            item.voltageHighCriticalLimit != null && item.voltage >= item.voltageHighCriticalLimit)
            return "critical";
        if (!item.temperature || (!item.temperatureHighCriticalLimit && !item.temperatureLowCriticalLimit && !item.temperatureHighWarningLimit && !item.temperatureLowWarningLimit))
            return "";
        if ((item.temperatureLowCriticalLimit != null && item.temperature <= item.temperatureLowCriticalLimit) ||
            item.temperatureHighCriticalLimit != null && item.temperature >= item.temperatureHighCriticalLimit)
            return "critical";
        if ((item.temperatureLowWarningLimit != null && item.temperature <= item.temperatureLowWarningLimit) ||
            item.temperatureHighWarningLimit != null && item.temperature >= item.temperatureHighWarningLimit)
            return "warning";
        return "";
    }
    get stringTableAllExpanded() {
        return !this.realtimeData.stringTable.find(item => item.expanded == false);
    }
    selectRow(componentId) {
        this.selectedRowComponentId = this.selectedRowComponentId === componentId ? null : componentId;
    }
    get getStringStates() {
        var _a;
        if (!((_a = this.realtimeData) === null || _a === void 0 ? void 0 : _a.stringsSummary))
            return [];
        return this.realtimeData.stringsSummary.map((summary) => ({
            stringId: summary.stringId,
            state: summary.stringStatus || 'Unknown'
        }));
    }
    showOhmicStringModal() {
        if (this.$refs.ohmicStringModal) {
            this.$refs.ohmicStringModal.show();
        }
    }
    getShIdByComponentId(componentId) {
        var _a, _b;
        if (!((_a = this.realtimeData) === null || _a === void 0 ? void 0 : _a.shIdToComponentIdMap))
            return null;
        const shId = (_b = Object.entries(this.realtimeData.shIdToComponentIdMap)
            .find(([key, value]) => value === componentId)) === null || _b === void 0 ? void 0 : _b[0];
        return shId ? parseInt(shId) : null;
    }
    handleOhmicStringSelection(stringId) {
        var _a, _b;
        if (!((_a = this.realtimeData) === null || _a === void 0 ? void 0 : _a.shIdToComponentIdMap))
            return;
        let shId = "";
        if (stringId > 0) {
            shId = this.getShIdByComponentId(stringId);
            if (!shId) {
                Notifier.onError('Unable to perform ohmic measurement. Invalid string mapping.');
                return;
            }
        }
        var requestModel = new RequestControllerForceOhmicRequestModel({
            systemId: ((_b = this.selectedSystem) === null || _b === void 0 ? void 0 : _b.systemId) || 0,
            componentShId: shId
        });
        this.sendRequestForceOhmic(requestModel);
    }
    ohmicBlockForce() {
        var _a;
        if (this.working)
            return;
        if (!this.selectedRowComponentId) {
            Notifier.onWarning('Please select a block to perform ohmic measurement.');
            return;
        }
        if (!((_a = this.realtimeData) === null || _a === void 0 ? void 0 : _a.shIdToComponentIdMap)) {
            Notifier.onError('Unable to perform ohmic measurement. Please try again later.');
            return;
        }
        const shId = this.getShIdByComponentId(this.selectedRowComponentId);
        if (!shId) {
            Notifier.onError('Unable to perform ohmic measurement. Invalid block mapping.');
            return;
        }
        this.$confirmModal.show("Are you sure you want to force an Ohmic value measurement? Note the measurement may take up to 60 seconds.", { title: "Ohmic Value Measurement" })
            .then((value) => {
            var _a;
            if (!value)
                return;
            var requestModel = new RequestControllerForceOhmicRequestModel({
                systemId: ((_a = this.selectedSystem) === null || _a === void 0 ? void 0 : _a.systemId) || 0,
                componentShId: shId
            });
            this.sendRequestForceOhmic(requestModel);
        });
    }
    sendRequestForceOhmic(model) {
        var command = new RequestControllerForceOhmicCommand({
            requestModel: model
        });
        dataTransferClient
            .forceOhmicMeasurements(command)
            .then((success) => {
            if (success)
                Notifier.onOk('Ohmic measurement command sent successfully.');
            else
                Notifier.onError('Failed to send ohmic measurement command.');
        }).
            catch((ex) => {
            HttpHelper.handleResponse(ex, () => {
                Notifier.onServerError(ex);
            });
        });
    }
    hasActiveImpedance(blockId) {
        var _a, _b, _c;
        return (_c = (_b = (_a = this.realtimeData) === null || _a === void 0 ? void 0 : _a.activeImpedanceBlockIds) === null || _b === void 0 ? void 0 : _b.includes(blockId)) !== null && _c !== void 0 ? _c : false;
    }
    isLithiumSamsungGen3Measurement(measurementType) {
        return this.realtimeData.isLithiumSamsungGen3 && this.moduleMeasurements.includes(measurementType);
    }
    ;
    exportToPdf() {
        if (this.working)
            return;
        if (!this.realtimeData) {
            Notifier.onWarning("Please select a system before downloading the report.");
            return;
        }
        var reportQuery = new GetSystemSnapshotPDFReportQuery({
            reportTimezoneOffSet: moment().utcOffset(),
            timezoneOffSet: moment().utcOffset(),
            systemId: this.realtimeData.systemId,
            measurementTime: new Date(),
            alarms: true,
            limits: true,
            tables: true,
            stringIds: [],
            realTimeData: true
        });
        this.working = true;
        this.isExporting = true;
        systemSnapshotClient.downloadSystemSnapshotAsPdf(reportQuery)
            .then(response => {
            if (!response) {
                Notifier.onWarning("Could not download snapshot data.");
                return;
            }
            //DownloadUtil.handleFileDownload(response);
            let blob = new Blob([response.data], { type: 'application/pdf;base64' });
            let url = URL.createObjectURL(blob);
            var popUp = window.open(url, '_blank');
            if (popUp == null || typeof (popUp) == 'undefined') {
                alert('Please disable your pop-up blocker and try again.');
            }
        })
            .catch(ex => {
            HttpHelper.handleResponse(ex, () => {
                Notifier.onServerError(ex);
            });
        })
            .finally(() => {
            this.working = false;
            this.isExporting = false;
        });
    }
    exportToCSV() {
        if (this.working)
            return;
        if (!this.realtimeData) {
            Notifier.onWarning("Please select a system before downloading the report.");
            return;
        }
        this.working = true;
        this.isExporting = true;
        let reportQuery = new GetSystemSnapshotCSVReportQuery({
            timezoneOffSet: moment().utcOffset(),
            currentTimezoneOffSet: moment().utcOffset(),
            systemId: this.realtimeData.systemId,
            measurementTime: new Date(),
            realTimeData: true
        });
        systemSnapshotClient.downloadSystemSnapshotAsCsv(reportQuery)
            .then(response => {
            if (!response) {
                Notifier.onWarning("Could not download snapshot data.");
                return;
            }
            DownloadUtil.handleFileDownload(response);
        })
            .catch(ex => {
            HttpHelper.handleResponse(ex, () => {
                Notifier.onServerError(ex);
            });
        })
            .finally(() => {
            this.working = false;
            this.isExporting = false;
        });
    }
};
__decorate([
    State("loginModule"),
    __metadata("design:type", typeof (_a = typeof LoginState !== "undefined" && LoginState) === "function" ? _a : Object)
], ControllerRealtimeDataVue.prototype, "loginState", void 0);
__decorate([
    Watch("selectedSystem"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], ControllerRealtimeDataVue.prototype, "onSystemChanged", null);
ControllerRealtimeDataVue = __decorate([
    Component({
        name: "ControllerRealtimeDataVue",
        components: {
            SnapshotGraphVue,
            LimitTooltipVue,
            OhmicStringSelectionModalVue
        }
    })
], ControllerRealtimeDataVue);
export default ControllerRealtimeDataVue;
